import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Truck, MapPin } from "lucide-react";
import { logError } from "@/lib/log";

const driverIcon = L.divIcon({
  className: "",
  html: `<div style="background:#16A34A;color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;font-family:'JetBrains Mono',monospace;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4)">●</div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

const poolIcon = L.divIcon({
  className: "",
  html: `<div style="background:#004EEB;color:#fff;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;border:3px solid #fff;box-shadow:0 1px 6px rgba(0,0,0,.4)">⌂</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});

function FitBoth({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 12);
      return;
    }
    map.fitBounds(points, { padding: [50, 50] });
  }, [map, points]);
  return null;
}

/**
 * Customer portal: live "Track your driver" section.
 * Only renders when a delivery is scheduled today. Shows the driver's live
 * position + ETA while they're on the road; polls every 30 seconds.
 */
export default function PortalDriverTracker() {
  const [track, setTrack] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/track").then((r) => setTrack(r.data)).catch((e) => logError("Track load failed:", e));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!track || track.reason === "no_delivery_today") return null;

  // Delivery today but driver not on the road yet
  if (!track.active) {
    return (
      <section className="border border-border rounded-sm" data-testid="portal-track-card">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-border">
          <Truck className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl font-bold tracking-tight">Your delivery today</h2>
        </div>
        <div className="p-6 text-sm text-muted-foreground" data-testid="portal-track-offline">
          A chlorine delivery is scheduled for today. Live driver tracking will appear here once the driver is on the road.
        </div>
      </section>
    );
  }

  const dPos = [Number(track.driver.latitude), Number(track.driver.longitude)];
  const dest = track.destination ? [Number(track.destination.latitude), Number(track.destination.longitude)] : null;
  const points = dest ? [dPos, dest] : [dPos];

  return (
    <section className="border border-primary/40 rounded-sm" data-testid="portal-track-card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4 text-primary" />
          <h2 className="font-display text-xl font-bold tracking-tight">Track your driver</h2>
        </div>
        {track.eta_minutes != null && (
          <div className="bg-primary text-primary-foreground rounded-sm px-4 py-1.5 text-sm font-bold" data-testid="portal-track-eta">
            ~{track.eta_minutes} min away{track.distance_miles != null ? ` · ${track.distance_miles} mi` : ""}
          </div>
        )}
      </div>
      <div className="h-[40vh] sm:h-[45vh] w-full bg-secondary/40">
        <MapContainer center={dPos} zoom={11} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={dPos} icon={driverIcon}>
            <Popup><div className="font-medium text-xs">{track.driver.name || "Your driver"}</div></Popup>
          </Marker>
          {dest && (
            <Marker position={dest} icon={poolIcon}>
              <Popup>
                <div className="text-xs">
                  <div className="font-bold">{track.destination.name || "Your pool"}</div>
                  <div className="text-muted-foreground">{track.destination.address || ""}</div>
                </div>
              </Popup>
            </Marker>
          )}
          <FitBoth points={points} />
        </MapContainer>
      </div>
      <div className="px-6 py-3 text-xs text-muted-foreground flex items-center gap-1.5">
        <MapPin className="w-3 h-3" /> Position updates every 30 seconds while the driver is on the road.
      </div>
    </section>
  );
}
